import { FC, useEffect, useState } from 'react';
import { CheckboxTickIcon, EditIconBlue, FormCloseIcon, InstitutionIcon, MapIcon } from 'assets/icons';
import { CustomButton, CustomCheckbox, CustomModal } from 'components';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { ButtonType } from 'components/custom-button/CustomButton';
import { CustomerObject } from 'containers/customers/customers.model';
import { getInitials } from 'utils/commonUtils';
import { ADDRESS_TYPE, initialAddressState } from 'containers/customers/constants';
import { VoidFn } from 'types/baseTypes';
import { getAddressAsString } from 'containers/jobs/utils';

interface CustomerDetailsModalProps {
  customer: CustomerObject;
  onOpenAddressModal: (params: { type: ADDRESS_TYPE; isOpen: boolean; isEdit: boolean; address }) => void;
  onClose: VoidFn;
  onOpenCustomerModal: (customer) => void;
  onUpdateCustomerDetails: (
    isServiceAddressEdit: boolean,
    isBillingAddressEdit: boolean,
    isBillingSameAsService: boolean
  ) => void;
  hasValuesChanged?: boolean;
  isUpdateInProgress?: boolean;
  onToggleBillingAddressCheck: (isSameAsServiceAddress: boolean) => void;
}

const CustomerDetailsModal: FC<CustomerDetailsModalProps> = ({
  customer,
  onOpenAddressModal,
  onClose,
  onOpenCustomerModal,
  onUpdateCustomerDetails,
  hasValuesChanged = true,
  isUpdateInProgress = false,
  onToggleBillingAddressCheck
}) => {
  const [isSameAsServiceAddress, setIsSameAsServiceAddress] = useState(false);
  const [isServiceAddressEdit, setIsServiceAddressEdit] = useState(false);
  const [isBillingAddressEdit, setIsBillingAddressEdit] = useState(false);

  useEffect(() => {
    setIsSameAsServiceAddress(customer.isBillingSameAsService);
  }, []);

  const toggleBillingAddressCheck = () => {
    onToggleBillingAddressCheck(!isSameAsServiceAddress);
    setIsSameAsServiceAddress(prev => !prev);
  };

  const handleOpenAddressModal = (type, isEdit, addressObject) => {
    onOpenAddressModal({ type, isOpen: true, isEdit, address: addressObject });
  };

  const handleOpenCustomerModal = () => {
    onOpenCustomerModal(customer);
  };

  const updateCustomer = () => {
    onUpdateCustomerDetails(isServiceAddressEdit, isBillingAddressEdit, isSameAsServiceAddress);
  };

  const shouldEnableUpdate = hasValuesChanged || isSameAsServiceAddress !== customer.isBillingSameAsService;

  return (
    <CustomModal open={true} width="md:w-[607px]" enableOutsideClick={false}>
      <div className="py-6">
        <div className="flex items-center justify-between px-6">
          <div className="text-xl font-bold text-heading ">Customer details</div>
          <FormCloseIcon className="cursor-pointer" role="presentation" onClick={onClose} width={24} height={24} />
        </div>
        <div className="py-6 px-8">
          <div className="flex items-center">
            <CustomAvatar
              src={''}
              label={getInitials(customer.name || customer.phoneNumber)}
              width="w-[72px]"
              height="h-[72px]"
              fontStyle="text-2xl text-primary font-bold leading-5 font-lato"
              background="bg-secondary"
            />
            <div className="ml-5">
              <div className="text-xl font-bold text-primaryText">
                {`${customer.name} ${customer.lastName}`.trim() ?? '-'}
              </div>
              <div className="text-sbase font-semibold text-primaryText">{customer.phoneNumber ?? '-'}</div>
              <div className="text-sbase font-semibold text-primaryText">{customer.email ?? '-'}</div>
            </div>
            <div className="ml-auto justify-self-end">
              <EditIconBlue className="cursor-pointer" role="presentation" onClick={handleOpenCustomerModal} />
            </div>
          </div>
          <div className="my-6 space-y-4 border-y border-borderGray py-6">
            <div className="flex items-center justify-between">
              <div className="text-17px font-bold text-heading">
                Service Address<span className="text-error">*</span>
              </div>
              {customer.serviceAddress?.length > 0 && (
                <EditIconBlue
                  className="cursor-pointer"
                  role="presentation"
                  onClick={() => {
                    setIsServiceAddressEdit(Boolean(customer.serviceAddress[0].id));
                    handleOpenAddressModal(ADDRESS_TYPE.SERVICE, true, customer.serviceAddress[0]);
                  }}
                />
              )}
            </div>
            {customer.serviceAddress?.length > 0 ? (
              <div className="flex items-start gap-1">
                <MapIcon className="path-fill-current path text-border" />
                <div>
                  <div className="text-sbase font-semibold text-primaryText">
                    {getAddressAsString(customer.serviceAddress[0]) ?? '-'}
                  </div>
                  {customer.serviceAddress[0].addressNote && (
                    <div className="text-sbase text-primaryText">{customer.serviceAddress[0].addressNote}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <InstitutionIcon className="path-fill-current path text-border" />
                <div
                  className="cursor-pointer text-17px font-semibold text-secondary"
                  role="presentation"
                  onClick={() => {
                    setIsServiceAddressEdit(false);
                    handleOpenAddressModal(ADDRESS_TYPE.SERVICE, false, initialAddressState);
                  }}>
                  + Add service address
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-17px font-bold text-heading">Billing Address</div>
              {customer.billingAddress && !isSameAsServiceAddress && (
                <EditIconBlue
                  className="cursor-pointer"
                  role="presentation"
                  onClick={() => {
                    setIsBillingAddressEdit(Boolean(customer.billingAddress.id));
                    handleOpenAddressModal(ADDRESS_TYPE.BILLING, true, customer.billingAddress);
                  }}
                />
              )}
            </div>
            <div className="mb-4 flex items-center">
              <CustomCheckbox
                id="same-as-service-address-checkbox"
                className="border-border"
                onClick={toggleBillingAddressCheck}
                isChecked={isSameAsServiceAddress}
                disabled={customer.serviceAddress?.length === 0}>
                {isSameAsServiceAddress && <CheckboxTickIcon />}
              </CustomCheckbox>
              <div className="ml-1 text-px13 font-semibold text-accentText">Same as service address</div>
            </div>
            {customer?.billingAddress ? (
              <div className="flex items-start gap-1">
                <MapIcon className="path-fill-current path text-border" />
                <div>
                  <div className="text-sbase font-semibold text-primaryText">
                    {getAddressAsString(customer.billingAddress) ?? '-'}
                  </div>
                  {customer.billingAddress.addressNote && (
                    <div className="text-sbase text-primaryText">{customer.billingAddress.addressNote}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <InstitutionIcon className="path-fill-current path text-border" />
                  <div
                    className="cursor-pointer text-17px font-semibold text-secondary"
                    role="presentation"
                    onClick={() => {
                      setIsBillingAddressEdit(false);
                      handleOpenAddressModal(ADDRESS_TYPE.BILLING, false, initialAddressState);
                    }}>
                    + Add billing address
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-8">
          <CustomButton
            id="customer-modal-cancel"
            className="h-10 w-[100px]"
            type={ButtonType.SECONDARY}
            onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            id="customer-modal-update"
            className="h-10 w-[100px]"
            type={ButtonType.PRIMARY}
            disabled={!shouldEnableUpdate}
            onClick={updateCustomer}
            isLoading={isUpdateInProgress}>
            Update
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default CustomerDetailsModal;
